import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, RequireAuth, SignInPage, SignInCallbackPage } from './auth';
import { ErrorBoundary, AppHeader } from './ui';
import { UploadPage } from './upload-page';
import { LibraryPage } from './library-page';
import { DetailPage } from './detail-page';
import { WatchPage } from './watch-page';
import { FeedPage } from './feed-page';
import { SharedLibraryPage } from './shared-library-page';
import { SharedVideoPage } from './shared-video-page';
import { EmbedPage } from './embed-page';
import { PlaylistsPage } from './playlists-page';
import { PlaylistDetailPage } from './playlist-detail-page';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});

function AuthedShell() {
  return (
    <div className="app-shell">
      <AppHeader />
      <Outlet />
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              {/* Public routes — must be reachable without sign-in. */}
              <Route path="/watch/:uploadId" element={<WatchPage />} />
              <Route path="/feed/:playlistId" element={<FeedPage />} />
              <Route path="/embed/:id" element={<EmbedPage />} />

              {/* Auth entry points. */}
              <Route path="/sign-in" element={<SignInPage />} />
              <Route path="/sign-in/callback" element={<SignInCallbackPage />} />

              {/* Authenticated app. */}
              <Route
                element={
                  <RequireAuth>
                    <AuthedShell />
                  </RequireAuth>
                }
              >
                <Route path="/" element={<Navigate to="/library" replace />} />
                <Route path="/library" element={<LibraryPage />} />
                <Route path="/library/:uploadId" element={<DetailPage />} />
                <Route path="/upload" element={<UploadPage />} />
                <Route path="/shared" element={<SharedLibraryPage />} />
                <Route path="/shared/:videoId" element={<SharedVideoPage />} />
                <Route path="/playlists" element={<PlaylistsPage />} />
                <Route path="/playlists/:playlistId" element={<PlaylistDetailPage />} />
              </Route>

              <Route path="*" element={<Navigate to="/library" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
